<template>
	<div :class="bemm()">
		<h3 :class="bemm('title')">{{ title }}</h3>
		<p v-if="message" :class="bemm('message')">{{ message }}</p>

		<BaseInput
			v-model="inputValue"
			:placeholder="placeholder"
			:type="type"
			:error="errorMessage"
			:class="bemm('input')"
			@keyup.enter="handleConfirm"
			ref="inputRef"
		/>

		<div :class="bemm('actions')">
			<Button color="secondary" @click="handleCancel">
				{{ cancelLabel }}
			</Button>
			<Button color="primary" @click="handleConfirm">
				{{ confirmLabel }}
			</Button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useBemm } from 'bemm';
import BaseInput from '../../Form/BaseInput.vue';
import { Button } from '../../Button';

interface Props {
	title: string;
	message?: string;
	placeholder?: string;
	initialValue?: string;
	confirmLabel?: string;
	cancelLabel?: string;
	validate?: (value: string) => boolean | string;
	type?: 'text' | 'email' | 'number' | 'password';
	onConfirm?: (value: string) => void;
	onCancel?: () => void;
	popupId?: string;
}

const props = withDefaults(defineProps<Props>(), {
	confirmLabel: 'OK',
	cancelLabel: 'Cancel',
	initialValue: '',
	type: 'text',
});

const bemm = useBemm('input-dialog', {
	return: 'string',
	includeBaseClass: true,
});
const inputValue = ref(props.initialValue);
const errorMessage = ref('');
const inputRef = ref<any>(null);

const handleConfirm = () => {
	const value = inputValue.value.trim();

	if (props.validate) {
		const validationResult = props.validate(value);
		if (validationResult !== true) {
			errorMessage.value =
				typeof validationResult === 'string'
					? validationResult
					: 'Invalid input';
			return;
		}
	}

	props.onConfirm?.(value);
};

const handleCancel = () => {
	props.onCancel?.();
};

onMounted(() => {
	// Focus the input when dialog opens
	setTimeout(() => {
		inputRef.value?.focus();
	}, 100);
});
</script>

<style lang="scss">
.input-dialog {
	padding: var(--space-l);
	min-width: 400px;
	max-width: 500px;

	&__title {
		margin: 0 0 var(--space-m) 0;
		font-size: var(--font-size-l);
		font-weight: var(--font-weight-semibold, var(--font-weight-bold));
	}

	&__message {
		margin: 0 0 var(--space-m) 0;
		color: color-mix(in srgb, var(--color-foreground), transparent 20%);
	}

	&__input {
		margin-bottom: var(--space-l);
		width: 100%;
	}

	&__actions {
		display: flex;
		justify-content: flex-end;
		gap: var(--space-s);
	}
}
</style>
